import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@loader/ui';
import { Loader2 } from 'lucide-react';

import { SchedulePicker } from './SchedulePicker';
import { DEFAULT_SCHEDULE } from './schedulePickerUtils';

interface ScheduleEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSchedule: string | null;
  onSave: (schedule: string | null) => Promise<void> | void;
}

export function ScheduleEditDialog({
  open,
  onOpenChange,
  currentSchedule,
  onSave,
}: ScheduleEditDialogProps) {
  const [schedule, setSchedule] = useState(currentSchedule ?? DEFAULT_SCHEDULE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSchedule(currentSchedule ?? DEFAULT_SCHEDULE);
  }, [open, currentSchedule]);

  const handleSave = async (value: string | null) => {
    setSaving(true);
    try {
      await onSave(value);
      onOpenChange(false);
    } catch {
      // Error already handled by caller (toast shown in useScheduleEditDialog)
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Schedule</DialogTitle>
          <DialogDescription>
            Choose how often this connector should sync.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <SchedulePicker value={schedule} onChange={setSchedule} />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            className="text-destructive mr-auto"
            disabled={saving}
            onClick={() => handleSave(null)}
          >
            Remove Schedule
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={saving}
            onClick={() => handleSave(schedule)}
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
